/**
 * useKeyboardShortcuts — global tek-noktalı klavye kısayolları.
 *
 * Bağlanan tuşlar:
 *   • P                → Sunum modunu aç/kapat
 *   • ?  (Shift+/)     → Kısayollar yardım modal'ı
 *   • Esc              → Sunum modu açıksa kapat (yardım modal'ı zaten Radix tarafından kapanır)
 *   • Cmd/Ctrl + K     → (Rezerve — gelecek komut paleti için)
 *
 * Kurallar:
 *   • Bir <input>, <textarea> veya contenteditable odaktayken hiçbir
 *     kısayol çalışmaz; metin girişini bozma.
 *   • IME composition aktifken yoksay.
 *   • document.body'ye bağlanır; tek bir useEffect, leak-free cleanup.
 */
import { useEffect } from "react";
import { useDebateStore } from "../store/debateStore";

function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (t.isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts(): void {
  const togglePresentMode = useDebateStore((s) => s.togglePresentMode);
  const presentMode = useDebateStore((s) => s.presentMode);
  const setPresentMode = useDebateStore((s) => s.setPresentMode);
  const toggleShortcuts = useDebateStore((s) => s.toggleShortcuts);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.isComposing) return;
      if (isEditableTarget(e.target)) return;
      // Ignore when ANY modifier is held except for the explicit combos we
      // handle below (currently none — modifier-bearing combos are reserved).
      if (e.altKey || e.metaKey || e.ctrlKey) return;

      switch (e.key) {
        case "p":
        case "P": {
          e.preventDefault();
          togglePresentMode();
          return;
        }
        case "?": {
          e.preventDefault();
          toggleShortcuts();
          return;
        }
        case "Escape": {
          if (presentMode) {
            e.preventDefault();
            setPresentMode(false);
          }
          return;
        }
        default:
          return;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [togglePresentMode, presentMode, setPresentMode, toggleShortcuts]);
}
